import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, openAuthorizedFile } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ToneBadge } from "./StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { QUOTE_STATUSES, QUOTE_LINE_TYPES, money } from "@/lib/constants";
import { Plus, Send, CheckCircle2, XCircle, Trash2, FileText, Download, Loader2 } from "lucide-react";

export function QuotesPanel({ serviceOrder, onChanged }) {
  const [quotes, setQuotes] = useState([]);
  const [parts, setParts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: "", notes: "", iva_rate: 0.16, valid_until: "", lines: [] });
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const { hasPermission } = useAuth();
  const canExport = hasPermission("quotes:export");

  async function downloadPdf(q) {
    if ((creating || editingId === q.id) &&
        !window.confirm("Tienes cambios sin guardar. El PDF mostrará la última versión guardada. ¿Descargar de todos modos?")) return;
    setDownloading(q.id);
    try {
      await openAuthorizedFile(`/service-orders/${serviceOrder.id}/quotes/${q.id}/pdf`, `Cotizacion_${(q.number || q.id).replace(/[^A-Za-z0-9_-]+/g, "-")}.pdf`);
    } catch (e) {
      toast.error(`No se pudo descargar el PDF: ${formatApiError(e)}`);
    } finally {
      setDownloading(null);
    }
  }

  async function load() {
    try {
      const [{ data: qs }, { data: ps }] = await Promise.all([
        api.get(`/service-orders/${serviceOrder.id}/quotes`),
        // roles without inventory:view (caja) must still see the quotes
        api.get("/parts").catch(() => ({ data: [] })),
      ]);
      setQuotes(qs);
      setParts(ps);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [serviceOrder.id]);

  function openNew() {
    setCreating(true); setEditingId(null);
    setForm({ title: "", notes: "", iva_rate: 0.16, valid_until: "", lines: [] });
  }
  function openEdit(q) {
    setCreating(true); setEditingId(q.id);
    setForm({
      title: q.title || "", notes: q.notes || "", iva_rate: q.iva_rate,
      valid_until: q.valid_until || "",
      lines: q.lines.map(l => ({ ...l })),
    });
  }
  function addLine(preset = {}) {
    setForm({ ...form, lines: [...form.lines, { line_type: "part", description: "", quantity: 1, unit_price: 0, unit: "pza", iva_rate: form.iva_rate, part_id: null, ...preset }] });
  }
  function updateLine(idx, patch) {
    const lines = [...form.lines];
    lines[idx] = { ...lines[idx], ...patch };
    // If part_id selected, auto-fill from parts
    if (patch.part_id) {
      const p = parts.find(x => x.id === patch.part_id);
      if (p) {
        lines[idx].description = p.name;
        lines[idx].unit_price = p.unit_price;
        lines[idx].unit = p.unit;
        lines[idx].iva_rate = p.iva_rate;
      }
    }
    setForm({ ...form, lines });
  }
  function removeLine(idx) {
    setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) });
  }

  const subtotalPreview = form.lines.reduce((sum, l) => sum + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
  const ivaPreview = form.lines.reduce((sum, l) => sum + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0) * (Number(l.iva_rate) || 0), 0);
  const totalPreview = subtotalPreview + ivaPreview;

  async function save() {
    setSaving(true);
    try {
      const payload = {
        title: form.title || null,
        notes: form.notes || null,
        iva_rate: Number(form.iva_rate) || 0.16,
        valid_until: form.valid_until || null,
        lines: form.lines.map((l, i) => ({
          line_type: l.line_type, part_id: l.part_id || null,
          description: l.description, quantity: Number(l.quantity) || 0,
          unit_price: Number(l.unit_price) || 0, unit: l.unit || "pza",
          iva_rate: Number(l.iva_rate) || 0.16, position: i,
        })),
      };
      if (editingId) {
        await api.patch(`/service-orders/${serviceOrder.id}/quotes/${editingId}`, payload);
        toast.success("Cotización actualizada");
      } else {
        await api.post(`/service-orders/${serviceOrder.id}/quotes`, payload);
        toast.success("Cotización creada");
      }
      setCreating(false); load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  }

  async function resendEmail(qId) {
    if (!window.confirm("¿Reenviar al cliente el aviso de esta cotización? Quedará registrado en el historial.")) return;
    try {
      await api.post(`/service-orders/${serviceOrder.id}/quotes/${qId}/resend-email`);
      toast.success("Reenvío del aviso registrado");
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function transition(qId, action) {
    if (action === "reject" && !window.confirm("¿Rechazar esta cotización?")) return;
    try {
      await api.post(`/service-orders/${serviceOrder.id}/quotes/${qId}/${action}`);
      toast.success(action === "send" ? "Cotización enviada" : action === "approve" ? "Cotización aprobada" : "Cotización rechazada");
      load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  return (
    <div className="space-y-4" data-testid="panel-quotes">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg tracking-tight">Cotizaciones</h3>
          <p className="text-xs text-muted-foreground">Una vez enviada, la cotización se congela. Para cambios, crea una nueva versión.</p>
        </div>
        {!creating && hasPermission("quotes:create") && <Button className="btn-tenant" size="sm" onClick={openNew} data-testid="btn-new-quote"><Plus className="h-4 w-4 mr-2" /> Nueva cotización</Button>}
      </div>

      {creating && (
        <Card className="p-5 border-border space-y-4" data-testid="quote-form">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="quote-title" /></div>
            <div className="space-y-2"><Label>Válida hasta</Label><Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="quote-valid" /></div>
          </div>
          <div className="border border-border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="p-2 text-left w-24">Tipo</th>
                  <th className="p-2 text-left">Concepto</th>
                  <th className="p-2 text-right w-20">Cant.</th>
                  <th className="p-2 text-right w-28">Precio</th>
                  <th className="p-2 text-right w-28">Subtotal</th>
                  <th className="p-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((l, i) => (
                  <tr key={i} className="border-t border-border" data-testid={`quote-line-${i}`}>
                    <td className="p-1.5">
                      <Select value={l.line_type} onValueChange={(v) => updateLine(i, { line_type: v, part_id: v === "part" ? l.part_id : null })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{QUOTE_LINE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-1.5">
                      {l.line_type === "part" ? (
                        <Select value={l.part_id || ""} onValueChange={(v) => updateLine(i, { part_id: v })}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                          <SelectContent>{parts.filter(p => p.status === "active").map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku})</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (
                        <Input value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} className="h-8" />
                      )}
                    </td>
                    <td className="p-1.5"><Input type="number" step="0.01" value={l.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} className="h-8 text-right" /></td>
                    <td className="p-1.5"><Input type="number" step="0.01" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: e.target.value })} className="h-8 text-right" /></td>
                    <td className="p-1.5 text-right mono text-xs">{money((Number(l.quantity) || 0) * (Number(l.unit_price) || 0))}</td>
                    <td className="p-1.5 text-center"><button onClick={() => removeLine(i)} className="text-red-600 hover:text-red-700" data-testid={`quote-line-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t border-border bg-slate-50">
                <tr><td colSpan={4} className="p-2 text-right text-muted-foreground">Subtotal</td><td className="p-2 text-right mono">{money(subtotalPreview)}</td><td></td></tr>
                <tr><td colSpan={4} className="p-2 text-right text-muted-foreground">IVA</td><td className="p-2 text-right mono">{money(ivaPreview)}</td><td></td></tr>
                <tr><td colSpan={4} className="p-2 text-right font-medium">Total</td><td className="p-2 text-right mono font-medium">{money(totalPreview)}</td><td></td></tr>
              </tfoot>
            </table>
            <div className="p-2 border-t border-border">
              <Button variant="outline" size="sm" onClick={() => addLine()} data-testid="quote-add-line"><Plus className="h-3.5 w-3.5 mr-1" /> Agregar línea</Button>
            </div>
          </div>
          <div className="space-y-2"><Label>Notas</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCreating(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || form.lines.length === 0} className="btn-tenant" data-testid="quote-save">{saving ? "Guardando…" : editingId ? "Guardar" : "Crear cotización"}</Button>
          </div>
        </Card>
      )}

      {quotes.length === 0 && !creating && (
        <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-8 text-center bg-white">
          <FileText className="h-6 w-6 mx-auto mb-2 text-slate-300" />
          Sin cotizaciones. Crea la primera para poder registrar anticipos y cerrar la venta.
        </div>
      )}

      <div className="space-y-3">
        {quotes.map((q) => (
          <Card key={q.id} className="border-border p-4" data-testid={`quote-${q.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="mono font-medium text-sm">{q.number}</span>
                  <ToneBadge list={QUOTE_STATUSES} value={q.status} />
                  {q.version > 1 && <span className="text-[11px] text-muted-foreground">v{q.version}</span>}
                </div>
                {q.title && <div className="text-sm">{q.title}</div>}
                <div className="text-xs text-muted-foreground">
                  {q.lines.length} concepto{q.lines.length === 1 ? "" : "s"} · Total <b className="text-slate-900">{money(q.total)}</b>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-wrap justify-end">
                {canExport && (
                  <Button size="sm" variant="outline" onClick={() => downloadPdf(q)} disabled={downloading === q.id} data-testid={`quote-pdf-${q.id}`}>
                    {downloading === q.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Descargar PDF
                  </Button>
                )}
                {q.status === "draft" && hasPermission("quotes:edit") && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => openEdit(q)} data-testid={`quote-edit-${q.id}`}>Editar</Button>
                    <Button size="sm" variant="outline" onClick={() => transition(q.id, "send")} data-testid={`quote-send-${q.id}`}><Send className="h-4 w-4 mr-1" /> Enviar</Button>
                  </>
                )}
                {q.status === "sent" && hasPermission("quotes:edit") && (
                  <Button size="sm" variant="outline" onClick={() => resendEmail(q.id)} data-testid={`quote-resend-email-${q.id}`}><Send className="h-4 w-4 mr-1" /> Reenviar aviso</Button>
                )}
                {(q.status === "draft" || q.status === "sent") && hasPermission("quotes:approve") && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => transition(q.id, "approve")} data-testid={`quote-approve-${q.id}`}><CheckCircle2 className="h-4 w-4 mr-1 text-emerald-600" /> Aprobar</Button>
                    <Button size="sm" variant="ghost" onClick={() => transition(q.id, "reject")} title="Rechazar cotización" aria-label="Rechazar cotización" data-testid={`quote-reject-${q.id}`}><XCircle className="h-4 w-4 text-red-600" /></Button>
                  </>
                )}
              </div>
            </div>
            <table className="w-full text-xs">
              <tbody>
                {q.lines.map((l) => (
                  <tr key={l.id} className="border-t border-border">
                    <td className="py-1.5 pr-2">{l.description}</td>
                    <td className="py-1.5 pr-2 text-muted-foreground text-right">{l.quantity} {l.unit}</td>
                    <td className="py-1.5 pr-2 mono text-right">{money(l.unit_price)}</td>
                    <td className="py-1.5 mono text-right">{money(l.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ))}
      </div>
    </div>
  );
}
